/**
 * Discover the layout targets (fields / columns / sections) a registered
 * component renders, by scanning the **live-preview DOM** for the stable
 * `data-testid` markers the integrated components already emit:
 *
 * - EntityForm fields  → `data-testid="entity-form-field-<name>"`
 * - EntityForm sections → `data-testid="entity-form-section-<id>"`
 * - DataList columns   → `data-testid="cell-<columnKey>"` (mobile card cells,
 *   which carry the column key; the desktop header cells do not, so the
 *   mobile markers are the reliable key source).
 * - DataList toolbar filters → `data-testid="filter-<id>"` (the rendered filter
 *   inputs carry the stable filter id).
 *
 * The toolbar **search** box (`data-testid="data-list-search"`) is intentionally
 * NOT scraped here: it carries no stable label and the editor needs its base
 * placeholder / enabled state, which only the published-config path
 * (`discoverConfigTargets`, the primary route) can supply. A DOM-discovered
 * filter likewise gets no `supportsPlaceholder` flag (the scrape can't read a
 * placeholder apart from a label) — the config path is what fully populates
 * filter/search targets.
 *
 * This keeps the authoring page entirely in its own lane: it reads the
 * already-rendered preview rather than reaching into the claimflow cards or
 * the engine for an introspection API. Components that render none of these
 * markers simply yield no discovered targets — the admin can still add a
 * target manually by typing its stable key.
 *
 * Pure DOM reading; no React. The single argument is the preview container
 * element (or `null` before it mounts), so the caller owns the ref.
 */

import type { LayoutTarget } from './layoutAuthoring';

/** Marker prefixes → target kind. Order is irrelevant; keys are de-duped. */
const FIELD_PREFIX = 'entity-form-field-';
const SECTION_PREFIX = 'entity-form-section-';
const COLUMN_CELL_PREFIX = 'cell-';
const FILTER_PREFIX = 'filter-';

/**
 * Read a human-friendly base label for a discovered target out of the
 * element's text content, trimmed and collapsed. Falls back to the key when
 * the element renders no readable text (e.g. an icon-only cell).
 */
function readLabel(el: Element, key: string): string {
  const text = el.textContent.replace(/\s+/g, ' ').trim();
  if (text === '') return key;
  // Field containers include the input value too; keep the label short by
  // taking the leading run before any newline-equivalent gap already removed.
  // We cap to a sane length so a whole field's contents never becomes a label.
  return text.length > 60 ? key : text;
}

/**
 * Scan a preview container for layout targets. De-dupes by `kind:key` so a
 * field that appears once yields one target even if its marker nests other
 * markers. Returns targets grouped in render order within each kind.
 */
export function discoverLayoutTargets(
  container: HTMLElement | null,
): LayoutTarget[] {
  if (container === null) return [];

  const seen = new Set<string>();
  const targets: LayoutTarget[] = [];

  const push = (kind: LayoutTarget['kind'], key: string, el: Element) => {
    if (key === '') return;
    const dedupeKey = `${kind}:${key}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    targets.push({
      kind,
      key,
      baseLabel: kind === 'section' ? key : readLabel(el, key),
    });
  };

  const nodes = container.querySelectorAll<HTMLElement>('[data-testid]');
  nodes.forEach((el) => {
    const testid = el.getAttribute('data-testid');
    if (testid === null) return;
    if (testid.startsWith(FIELD_PREFIX)) {
      push('field', testid.slice(FIELD_PREFIX.length), el);
    } else if (testid.startsWith(SECTION_PREFIX)) {
      push('section', testid.slice(SECTION_PREFIX.length), el);
    } else if (testid.startsWith(FILTER_PREFIX)) {
      push('filter', testid.slice(FILTER_PREFIX.length), el);
    } else if (testid.startsWith(COLUMN_CELL_PREFIX)) {
      push('column', testid.slice(COLUMN_CELL_PREFIX.length), el);
    }
  });

  return targets;
}

/** Filter discovered targets to one kind, preserving discovery order. */
export function targetsOfKind(
  targets: readonly LayoutTarget[],
  kind: LayoutTarget['kind'],
): LayoutTarget[] {
  return targets.filter((t) => t.kind === kind);
}
